import type { ReactNode } from 'react';

export interface StatusSegment {
  key: string;
  label: string;
  /** A literal colour, shared with the bar's inline width style. */
  colour: string;
  count: number;
  /** The figure under the label; the count alone when absent. */
  value?: ReactNode;
  /** What the bar measures this segment by; left out of the bar when absent. */
  weight?: number;
}

/**
 * A compact status card for the top of a list on a phone or tablet: the
 * headline figure, one stacked bar, then each status's count and value.
 *
 * Deliberately short, so the list it summarises stays on the first screen —
 * the donut it replaced pushed the list below the fold.
 */
export function StatusSummary({
  title,
  headline,
  caption,
  aside,
  segments,
  unit = ['item', 'items'],
  active,
  onSelect,
}: {
  title: string;
  headline: ReactNode;
  caption?: string;
  aside?: ReactNode;
  segments: StatusSegment[];
  /** What a count counts, singular then plural. */
  unit?: [string, string];
  /** The segment the list below is filtered to, if any. */
  active?: string;
  /** Makes each status a filter for the list below. */
  onSelect?: (key: string) => void;
}) {
  const total = segments.reduce((sum, segment) => sum + (segment.weight ?? 0), 0);

  return (
    <section className="card mb-4 p-4" aria-label={title}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="stat-label">{title}</p>
          <p className="mt-0.5 text-xl font-semibold tabular text-ink-900">
            {headline}
            {caption ? (
              <span className="ml-1.5 text-sm font-normal text-ink-500">{caption}</span>
            ) : null}
          </p>
        </div>
        {aside ? <div className="shrink-0 text-right text-sm text-ink-500">{aside}</div> : null}
      </div>

      <div
        className="mt-2.5 flex h-2 gap-0.5 overflow-hidden rounded-full bg-ink-100"
        aria-hidden="true"
      >
        {total > 0
          ? segments.map((segment) =>
              segment.weight ? (
                <span
                  key={segment.key}
                  className="h-full first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${(segment.weight / total) * 100}%`,
                    backgroundColor: segment.colour,
                  }}
                />
              ) : null,
            )
          : null}
      </div>

      <ul className="-mx-2 mt-2 grid grid-cols-3 gap-x-1 gap-y-0.5 md:grid-cols-6">
        {segments.map((segment) => {
          const selected = active === segment.key;
          const body = (
            <>
              <span className="flex items-center gap-1.5 text-xs text-ink-500">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: segment.colour }}
                  aria-hidden="true"
                />
                <span className="truncate">{segment.label}</span>
              </span>
              <span className="mt-0.5 flex items-baseline gap-1 truncate tabular">
                <span className="text-sm font-semibold text-ink-900">
                  {segment.value ?? segment.count}
                </span>
                {segment.value !== undefined ? (
                  <span className="text-[11px] text-ink-400">
                    · {segment.count}
                    <span className="sr-only"> {segment.count === 1 ? unit[0] : unit[1]}</span>
                  </span>
                ) : null}
              </span>
            </>
          );
          const box = `block w-full rounded-lg px-2 py-1 text-left ${
            selected ? 'bg-brand-50 ring-1 ring-brand-300' : ''
          }`;
          return (
            <li key={segment.key} className="min-w-0">
              {onSelect ? (
                <button
                  type="button"
                  className={`${box} hover:bg-ink-50`}
                  aria-pressed={selected}
                  onClick={() => onSelect(segment.key)}
                >
                  {body}
                </button>
              ) : (
                <div className={box}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
